import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Send, ExternalLink } from "lucide-react";
import { SOCIALS } from "@/data/mock";
import { useSiteConfig } from "@/context/SiteConfig";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const NAV = [
  { label: "Início", href: "#inicio" },
  { label: "Ao Vivo", href: "#rtp" },
  { label: "Jogos", href: "#jogos" },
  { label: "Ranking", href: "#ranking" },
  { label: "Sinais", href: "#sinais" },
  { label: "Crash", href: "/crash" },
  { label: "Estatísticas", href: "#estatisticas" },
  { label: "Contato", href: "#contato" },
];

export const Header = () => {
  const cfg = useSiteConfig();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  // Extra menu entry — "Acesso a todas as plataformas" (editável no admin)
  const allPlatformsLabel = cfg.all_platforms_label || "Acesso a todas as plataformas";
  const allPlatformsUrl = cfg.all_platforms_url || "https://bio.site/MangoGroup";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("http")) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    if (href.startsWith("/")) {
      window.location.href = href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-[#120D0D]/80 backdrop-blur-xl border-b border-white/[0.06]"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => go("#inicio")}
            className="flex items-center gap-3 group"
            data-testid="header-logo"
          >
            <div className="relative">
              <div className="absolute inset-0 radial-gold scale-150 blur-md" />
              <img
                src="/manga-symbol.png"
                alt="Grupo Manga"
                className="relative w-11 h-11 object-contain gold-glow group-hover:scale-110 transition-transform duration-300"
              />
            </div>
            <div className="text-left leading-none">
              <div className="font-display text-base md:text-lg font-extrabold tracking-tight text-white">
                GRUPO <span className="text-gold-gradient">MANGA</span>
              </div>
              <div className="text-[9px] md:text-[10px] tracking-[0.2em] text-white/40 uppercase mt-1">
                Sinais • Análises • Plataformas Oficiais
              </div>
            </div>
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <button
                key={n.href}
                onClick={() => go(n.href)}
                className="px-3 py-2 text-sm font-medium text-white/60 hover:text-[#F5C542] transition-colors duration-300"
                data-testid={`nav-${n.href.replace("#", "")}`}
              >
                {n.label}
              </button>
            ))}
            {allPlatformsUrl && (
              <button
                onClick={() => go(allPlatformsUrl)}
                className="ml-2 inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold uppercase tracking-wider rounded-full btn-gold hover:scale-105 transition-transform"
                data-testid="nav-all-platforms"
              >
                {allPlatformsLabel} <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={cfg.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center bg-white/5 border border-white/10 text-white/70 hover:text-[#F5C542] hover:border-[#F5C542]/50 transition-colors duration-300"
              aria-label="Instagram"
              data-testid="header-instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href={cfg.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center bg-white/5 border border-white/10 text-white/70 hover:text-[#F5C542] hover:border-[#F5C542]/50 transition-colors duration-300"
              aria-label="Telegram"
              data-testid="header-telegram"
            >
              <Send className="w-4 h-4" />
            </a>
            {cfg.whatsapp && (
              <a
                href={cfg.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center bg-white/5 border border-white/10 text-white/70 hover:text-[#25D366] hover:border-[#25D366]/50 transition-colors duration-300"
                aria-label="WhatsApp"
                data-testid="header-whatsapp"
              >
                <WhatsAppIcon className="w-4 h-4" />
              </a>
            )}
            <button
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-white"
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-[72px] inset-x-0 z-40 lg:hidden bg-[#120D0D]/95 backdrop-blur-xl border-b border-white/10"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col p-4">
              {NAV.map((n) => (
                <button
                  key={n.href}
                  onClick={() => go(n.href)}
                  className="text-left px-4 py-3 text-white/70 hover:text-[#F5C542] font-medium border-b border-white/5 transition-colors"
                  data-testid={`mobile-nav-${n.href.replace("#", "")}`}
                >
                  {n.label}
                </button>
              ))}
              {allPlatformsUrl && (
                <button
                  onClick={() => go(allPlatformsUrl)}
                  className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-full btn-gold font-display font-bold text-sm uppercase tracking-wider"
                  data-testid="mobile-nav-all-platforms"
                >
                  {allPlatformsLabel} <ExternalLink className="w-4 h-4" />
                </button>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
